import { Step } from "../../types/TransactionTemplate.types";
import { convertTransactionToSteps } from "./transaction.converters";
import { ToastService } from "../../service/toast.service";
import { getLogicalBlockFromConnections } from "../../hooks/transactions/useDragPrimitive";

function reachableFrom(
  connections: Step["connections"],
  startId: string,
): Set<string> {
  const visited = new Set<string>();
  const queue = [startId];
  while (queue.length) {
    const cur = queue.shift()!;
    if (visited.has(cur)) continue;
    visited.add(cur);
    connections.filter((c) => c.from === cur).forEach((c) => queue.push(c.to));
  }
  return visited;
}

function findMergePoint(
  primitives: Step["primitives"],
  connections: Step["connections"],
  startId: string,
): string | null {
  const block = getLogicalBlockFromConnections(
    primitives,
    connections,
    startId,
  );
  const startPrim = primitives.find((p) => p.id === startId);

  if (startPrim?.type === "loop") {
    const afterConn = connections.find(
      (c) => c.from === startId && !block.has(c.to),
    );
    return afterConn?.to ?? null;
  }

  const exitCounts: Record<string, number> = {};
  connections
    .filter((c) => block.has(c.from) && !block.has(c.to))
    .forEach((c) => {
      exitCounts[c.to] = (exitCounts[c.to] || 0) + 1;
    });

  const mergeEntry = Object.entries(exitCounts).find(([, cnt]) => cnt > 1);
  if (mergeEntry) return mergeEntry[0];

  const exitTargets = Object.keys(exitCounts);
  return exitTargets[0] ?? null;
}

interface ActionEntry {
  list: any[];
  index: number;
}

export function buildActionTreeWithMap(
  step: Step,
  connections: Step["connections"],
): { actions: any[]; actionMap: Map<string, ActionEntry> } {
  const { primitives } = step;
  const actionMap = new Map<string, ActionEntry>();
  const globalVisited = new Set<string>();

  function buildChain(
    startId: string | null,
    targetList: any[],
    stopAt: Set<string>,
    debugPath: string,
  ): void {
    let currentId: string | null = startId;

    while (currentId !== null) {
      if (stopAt.has(currentId)) {
        break;
      }
      if (globalVisited.has(currentId)) {
        break;
      }
      globalVisited.add(currentId);

      const prim = primitives.find((p) => p.id === currentId);
      if (!prim) {
        break;
      }

      let action: any;
      if (prim.type === "condition") {
        action = {
          ...prim.data,
          type: "if",
          cond: prim.data?.cond ?? "",
          yesLine: [] as any[],
          noLine: [] as any[],
        };
      } else if (prim.type === "loop") {
        action = { ...prim.data, type: "for", bodyLine: [] as any[] };
      } else {
        action = { ...prim.data };
      }

      actionMap.set(currentId, { list: targetList, index: targetList.length });
      targetList.push(action);

      if (prim.type === "condition") {
        const merge = findMergePoint(primitives, connections, currentId);
        const block = getLogicalBlockFromConnections(
          primitives,
          connections,
          currentId,
        );

        const branchStop = new Set<string>(stopAt);
        if (merge) branchStop.add(merge);

        const yesConn = connections.find(
          (c) => c.from === currentId && c.branch === "yes",
        );
        const noConn = connections.find(
          (c) => c.from === currentId && c.branch === "no",
        );

        const beforeBranches = new Set(globalVisited);

        if (yesConn) {
          buildChain(
            yesConn.to,
            action.yesLine,
            branchStop,
            `${debugPath}/yes`,
          );
        }

        const afterYes = new Set(globalVisited);
        globalVisited.clear();
        beforeBranches.forEach((id) => globalVisited.add(id));

        if (noConn) {
          buildChain(noConn.to, action.noLine, branchStop, `${debugPath}/no`);
        }

        afterYes.forEach((id) => globalVisited.add(id));

        if (merge) {
          globalVisited.delete(merge);
          currentId = merge;
          continue;
        }
        break;
      }

      if (prim.type === "loop") {
        const merge = findMergePoint(primitives, connections, currentId);
        const branchStop = new Set<string>(stopAt);
        if (merge) branchStop.add(merge);

        const block = getLogicalBlockFromConnections(
          primitives,
          connections,
          currentId,
        );

        const bodyConns = connections.filter(
          (c) => c.from === currentId && block.has(c.to) && c.to !== currentId,
        );

        for (const bodyConn of bodyConns) {
          buildChain(
            bodyConn.to,
            action.bodyLine,
            branchStop,
            `${debugPath}/body`,
          );
        }

        if (merge) {
          globalVisited.delete(merge);
          currentId = merge;
          continue;
        }
        break;
      }

      const nextConn = connections.find(
        (c) => c.from === currentId && !c.branch,
      );
      currentId = nextConn?.to ?? null;
    }
  }

  const firstConn = connections.find((c) => c.from === step.event?.id);
  const actions: any[] = [];
  if (firstConn) buildChain(firstConn.to, actions, new Set<string>(), "root");

  return { actions, actionMap };
}

export function computeSwappedStep(
  stepIndex: number,
  step: Step,
  draggedId: string,
  targetId: string,
  originalPositions: Map<string, { x: number; y: number }>,
  originalConnections: Step["connections"],
  selectedTransactionId: number | null,
  t: (key: string) => string,
): Step | null {
  const { primitives } = step;
  const connections = originalConnections;

  const blockA = getLogicalBlockFromConnections(
    primitives,
    connections,
    draggedId,
  );
  const blockB = getLogicalBlockFromConnections(
    primitives,
    connections,
    targetId,
  );

  const draggedIsInsideB = blockB.has(draggedId) && draggedId !== targetId;
  const targetIsInsideA = blockA.has(targetId) && targetId !== draggedId;

  if (draggedIsInsideB || targetIsInsideA) {
    const sharedParent = primitives.find((p) => {
      if (p.id === draggedId || p.id === targetId) return false;
      if (p.type !== "condition" && p.type !== "loop") return false;
      const reach = reachableFrom(connections, p.id);
      return reach.has(draggedId) && reach.has(targetId);
    });

    if (!sharedParent) {
      ToastService.error(t("Нельзя менять местами вложенные элементы!"));
      return null;
    }
  }

  const { actions: rootActions, actionMap } = buildActionTreeWithMap(
    step,
    connections,
  );

  const foundA = actionMap.get(draggedId);
  const foundB = actionMap.get(targetId);

  if (!foundA || !foundB) {
    console.warn("[swap] primitive not found in actionMap", {
      draggedId,
      targetId,
      keys: [...actionMap.keys()],
    });
    return null;
  }

  const temp = foundA.list[foundA.index];
  foundA.list[foundA.index] = foundB.list[foundB.index];
  foundB.list[foundB.index] = temp;

  const recalculated = convertTransactionToSteps({
    id: selectedTransactionId ?? 0,
    actions: rootActions,
    queues: {},
  });

  const recalcStep = recalculated.steps[0];
  if (!recalcStep) return null;

  return {
    ...step,
    primitives: recalcStep.primitives,
    connections: recalcStep.connections,
    event: recalcStep.event,
  };
}
